import { c as _c } from "react/compiler-runtime";
import type { ApiNodeData, ApiOnboardingGraph } from '@onefootprint/request-types/dashboard';
import { useTranslation } from 'react-i18next';
import { useGetStatusesWithDeactivated } from '../../../hooks/use-get-statuses';
import type { NodeData } from '../../../playbook-builder.types';
import { nodeIconConfigs } from '../components/node-icon-config';
import { getActionIconColor, getActionSubtitle } from './node-info/action';
import { getAiDecisionIcon } from './node-info/ai-decision';
import { getCollectDataSubtitle } from './node-info/collect-data';
import { getCollectDocumentSubtitle } from './node-info/collect-document';
import { getCustomDocumentIcon, getCustomDocumentSubtitle } from './node-info/custom-document';
import { getIdentityDocumentSubtitle } from './node-info/identity-document';
import { getVerificationCheckSubtitle } from './node-info/verification-check';
import { type NodeInfo, useGetStaticNodeInfo } from './use-get-static-node-info';
export type { NodeInfo };
export const useGetNodeInfo = graph => {
  const $ = _c(8);
  const getStaticInfo = useGetStaticNodeInfo();
  const {
    t
  } = useTranslation("playbook-builder");
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = {
      keyPrefix: "id_document"
    };
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  const {
    t: tDocs
  } = useTranslation("common", t0);
  let t1;
  if ($[1] !== graph.nodes) {
    t1 = graph.nodes.flatMap(_temp).map(_temp2);
    $[1] = graph.nodes;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const currentStatusIds = t1;
  const statuses = useGetStatusesWithDeactivated(currentStatusIds);
  let t2;
  if ($[3] !== getStaticInfo || $[4] !== statuses || $[5] !== t || $[6] !== tDocs) {
    t2 = data => {
      const base = getStaticInfo(data.kind);
      switch (data.kind) {
        case "action":
          {
            return {
              ...base,
              subtitle: getActionSubtitle(data, t, statuses),
              icon: {
                ...nodeIconConfigs.action,
                color: getActionIconColor(data)
              }
            };
          }
        case "verification_check":
          {
            return {
              ...base,
              subtitle: getVerificationCheckSubtitle(data, t)
            };
          }
        case "collect_document":
          {
            return {
              ...base,
              subtitle: getCollectDocumentSubtitle(data, t)
            };
          }
        case "identity_document":
          {
            return {
              ...base,
              subtitle: getIdentityDocumentSubtitle(data, t, doc => tDocs($_3 => $_3[doc]))
            };
          }
        case "collect_data":
          {
            return {
              ...base,
              subtitle: getCollectDataSubtitle(data, t)
            };
          }
        case "ai_decision":
          {
            return {
              ...base,
              icon: getAiDecisionIcon(data),
              subtitle: data.name ?? base.subtitle
            };
          }
        case "custom_document":
          {
            return {
              ...base,
              icon: getCustomDocumentIcon(data),
              subtitle: getCustomDocumentSubtitle(data, t)
            };
          }
        case "ai_research":
          {
            return {
              ...base,
              subtitle: data.name ?? base.subtitle
            };
          }
        case "conditional":
          {
            return {
              ...base,
              subtitle: data.name ?? ""
            };
          }
        case "rules":
          {
            return {
              ...base,
              subtitle: data.name ?? ""
            };
          }
        case "taktile":
          {
            return {
              ...base,
              subtitle: getTaktileSubtitle(data, t)
            };
          }
        case "input_schema":
          {
            return {
              ...base,
              subtitle: getInputSchemaSubtitle(data, t)
            };
          }
        case "prerequisite":
          {
            return {
              ...base,
              subtitle: getPrerequisiteSubtitle(data)
            };
          }
        case "output":
          {
            return {
              ...base,
              subtitle: getOutputSubtitle(data, t)
            };
          }
        case "complete":
          {
            return {
              ...base,
              subtitle: data.page.header || base.subtitle
            };
          }
        case "branch":
          {
            const branchCtx = data.context?.context;
            const displayName = branchCtx?.kind === "branch" ? branchCtx.displayName : undefined;
            return {
              ...base,
              subtitle: displayName ?? base.subtitle
            };
          }
        case "external_api":
          {
            const isVendorIntegration = typeof data.source === "object" && "vendorIntegration" in data.source;
            const info = isVendorIntegration ? getStaticInfo("vendor_integration") : base;
            return {
              ...info,
              subtitle: data.name ?? info.subtitle
            };
          }
        case "assign_data":
          {
            return {
              ...base,
              subtitle: getAssignDataSubtitle(data, t)
            };
          }
        case "common_expression":
          {
            return {
              ...base,
              subtitle: data.name || t(_temp3)
            };
          }
        case "code_exec":
          {
            return {
              ...base,
              subtitle: data.name || t(_temp4)
            };
          }
        case "document_extraction":
          {
            return {
              ...base,
              subtitle: data.name ?? base.subtitle
            };
          }
        case "collect_custom_data":
          {
            return {
              ...base,
              subtitle: data.page.header || t(_temp5)
            };
          }
        case "verified_prefill":
          {
            return {
              ...base,
              subtitle: t(_temp6)
            };
          }
        case "authentication":
          {
            return {
              ...base,
              subtitle: getAuthenticationSubtitle(data, t)
            };
          }
        default:
          {
            return base;
          }
      }
    };
    $[3] = getStaticInfo;
    $[4] = statuses;
    $[5] = t;
    $[6] = tDocs;
    $[7] = t2;
  } else {
    t2 = $[7];
  }
  return t2;
};
const getTaktileSubtitle = (data: Extract<ApiNodeData, {
  kind: 'taktile';
}>, t: ReturnType<typeof useTranslation<'playbook-builder'>>['t']): string => {
  if (!data.flowSlug) return t($ => $.taktile.untitled);
  return data.flowVersion ? `${data.flowSlug} v${data.flowVersion}` : data.flowSlug;
};
const getInputSchemaSubtitle = (data: Extract<ApiNodeData, {
  kind: 'input_schema';
}>, t: ReturnType<typeof useTranslation<'playbook-builder'>>['t']): string => {
  const keys = Object.keys(data.schema);
  return keys.length === 0 ? t($ => $.inputSchema.untitled) : keys.join(', ');
};
const getPrerequisiteSubtitle = (data: Extract<ApiNodeData, {
  kind: 'prerequisite';
}>): string => data.prerequisites.map(p => p.kind === 'vault' ? p.di : `data.${p.key}`).join(', ');
const getOutputSubtitle = (data: Extract<ApiNodeData, {
  kind: 'output';
}>, t: ReturnType<typeof useTranslation<'playbook-builder'>>['t']): string => {
  const keys = data.outputs.map(p => p.dataKey).filter(Boolean);
  return keys.length > 0 ? keys.join(', ') : t($ => $.outputNode.untitled);
};
const getAssignDataSubtitle = (data: Extract<ApiNodeData, {
  kind: 'assign_data';
}>, t: ReturnType<typeof useTranslation<'playbook-builder'>>['t']): string => data.dataAssignments.length > 0 ? data.dataAssignments.map(a => a.destination.vault).join(', ') : t($ => $.dataAssignmentNode.subtitle);
const getAuthenticationSubtitle = (data: Extract<ApiNodeData, {
  kind: 'authentication';
}>, t: ReturnType<typeof useTranslation<'playbook-builder'>>['t']): string => {
  const labels: Record<string, string> = {
    phone: t($ => $.authentication.methods.phone.label),
    email: t($ => $.authentication.methods.email.label)
  };
  return data.configs.map(m => labels[m.kind] ?? m.kind).join(', ');
};
function _temp(node) {
  return node.kind === "action" ? node.setEntityStatuses ?? [] : [];
}
function _temp2(s) {
  return s.statusId;
}
function _temp3($_2) {
  return $_2.expressionNode.untitled;
}
function _temp4($_1) {
  return $_1.codeExecNode.untitled;
}
function _temp5($_0) {
  return $_0.collectCustomData.untitled;
}
function _temp6($) {
  return $.verifiedPrefill.subtitle;
}